import React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';

const StatCard = ({ title, value, subtitle, color = 'primary' }) => (
  <Paper sx={{ p: 2, height: '100%' }}>
    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="h4" component="div" color={color}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="textSecondary">
        {subtitle}
      </Typography>
    )}
  </Paper>
);

const ProgressCard = ({ title, value, total, color = 'primary' }) => {
  const percentage = (value / total) * 100;
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ flexGrow: 1, mr: 1 }}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            color={color}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
        <Typography variant="body2" color="textSecondary">
          {`${Math.round(percentage)}%`}
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary">
        {`${value} of ${total}`}
      </Typography>
    </Paper>
  );
};

const AIOrchestratorStats = ({ stats }) => {
  const {
    taskStats = {},
    memoryStats = {},
    agentStats = {},
    recentActivity = []
  } = stats;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Orchestrator Statistics
      </Typography>

      <Grid container spacing={3}>
        {/* Task Statistics */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Task Statistics
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Tasks"
            value={taskStats.total || 0}
            subtitle="All time"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Completed Tasks"
            value={taskStats.completed || 0}
            subtitle="Successfully executed"
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Failed Tasks"
            value={taskStats.failed || 0}
            subtitle="With errors"
            color="error"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Pending Tasks"
            value={taskStats.pending || 0}
            subtitle="In queue"
            color="warning"
          />
        </Grid>

        {/* Memory Statistics */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Memory Statistics
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Total Memory Entries"
            value={memoryStats.total || 0}
            subtitle="Stored entries"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Average Relevance"
            value={`${((memoryStats.avgRelevance || 0) * 100).toFixed(1)}%`}
            subtitle="Content relevance score"
            color="info"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard
            title="Average Quality"
            value={`${((memoryStats.avgQuality || 0) * 100).toFixed(1)}%`}
            subtitle="Content quality score"
            color="info"
          />
        </Grid>

        {/* Agent Statistics */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Agent Statistics
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Active Agents"
            value={agentStats.active || 0}
            total={agentStats.total || 0}
            color="success"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProgressCard
            title="Agent Utilization"
            value={agentStats.utilization || 0}
            total={100}
            color="primary"
          />
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Recent Activity
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Timeline>
              {recentActivity.map((activity, index) => (
                <TimelineItem key={index}>
                  <TimelineSeparator>
                    <TimelineDot color={activity.type === 'success' ? 'success' : 'primary'} />
                    {index < recentActivity.length - 1 && <TimelineConnector />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="body2">
                      {activity.message}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(activity.timestamp).toLocaleString()}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AIOrchestratorStats; 